import React, { useState, useEffect, useCallback } from 'react';
import {
  TicketIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  TrashIcon,
  FlagIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import { useAuth } from '../contexts/AuthContext';
import {
  supportTicketService,
  SupportTicket,
  SupportTicketStatus,
} from '../services/supportTicketService';
import { adminService, AdminUserRecord } from '../services/adminService';
import OrderService, { Order } from '../services/orderService';
import './AdminPanel.css';

type AdminTab = 'tickets' | 'users' | 'orders';
type TicketFilter = 'all' | SupportTicketStatus;
type UserFilter = 'all' | 'customer' | 'vendor' | 'admin' | 'active' | 'suspended';
type OrderFilter = 'all' | Order['paymentStatus'] | Order['status'];

const AdminPanel: React.FC = () => {
  const { user, userAccountType } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>('tickets');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ticketFilter, setTicketFilter] = useState<TicketFilter>('all');
  const [userFilter, setUserFilter] = useState<UserFilter>('all');
  const [orderFilter, setOrderFilter] = useState<OrderFilter>('succeeded');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [ticketStatus, setTicketStatus] = useState<SupportTicketStatus>('open');
  const [ticketNotes, setTicketNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!user || userAccountType !== 'admin') return;

    try {
      setLoading(true);
      setError(null);
      const orderService = OrderService.getInstance();
      const [ticketData, userData, orderData] = await Promise.all([
        supportTicketService.getAllTickets(),
        adminService.getAllUsers(),
        orderService.getAllOrders(),
      ]);
      setTickets(ticketData);
      setUsers(userData);
      setOrders(orderData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load admin data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [user, userAccountType]);

  useEffect(() => {
    if (!user || userAccountType !== 'admin') {
      setLoading(false);
      return;
    }
    loadData();
  }, [user, userAccountType, loadData]);

  const filteredTickets = tickets.filter(
    (ticket) => ticketFilter === 'all' || ticket.status === ticketFilter
  );

  const filteredUsers = users.filter((record) => {
    if (userFilter === 'all') return true;
    if (userFilter === 'active') return record.isActive;
    if (userFilter === 'suspended') return !record.isActive;
    return record.accountType === userFilter;
  });

  const completedOrders = orders.filter((order) => order.paymentStatus === 'succeeded');

  const filteredOrders = orders.filter((order) => {
    if (orderFilter === 'all') {
      return order.paymentStatus !== 'failed' && order.status !== 'cancelled';
    }
    if (orderFilter === 'pending' || orderFilter === 'succeeded' || orderFilter === 'failed') {
      return order.paymentStatus === orderFilter;
    }
    return order.status === orderFilter;
  });

  const openTickets = tickets.filter((t) => t.status === 'open').length;
  const suspendedUsers = users.filter((u) => !u.isActive && u.accountType !== 'admin').length;
  const totalOrderRevenue = completedOrders.reduce((sum, order) => sum + order.totalAmount, 0);

  const formatProductsSummary = (order: Order) => {
    const names = order.items.map((item) => item.name);
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
  };

  const formatVendorsSummary = (order: Order) =>
    order.vendorOrders.map((vo) => vo.vendorName).join(', ');

  const getPaymentStatusBadge = (status: Order['paymentStatus']) => (
    <span className={`status-badge payment-${status}`}>{status}</span>
  );

  const getOrderStatusBadge = (status: Order['status']) => (
    <span className={`status-badge order-${status}`}>{status}</span>
  );

  const openTicketModal = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setTicketStatus(ticket.status);
    setTicketNotes(ticket.adminNotes || '');
  };

  const handleSaveTicket = async () => {
    if (!selectedTicket) return;
    try {
      setActionLoading(true);
      await supportTicketService.updateTicket(selectedTicket.id, {
        status: ticketStatus,
        adminNotes: ticketNotes,
      });
      setSelectedTicket(null);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update ticket');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspendUser = async (userId: string) => {
    if (!window.confirm('Suspend this user? They will not be able to log in.')) return;
    try {
      setActionLoading(true);
      await adminService.suspendUser(userId);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to suspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspendUser = async (userId: string) => {
    try {
      setActionLoading(true);
      await adminService.unsuspendUser(userId);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reactivate user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (record: AdminUserRecord) => {
    if (
      !window.confirm(
        `Permanently delete ${record.email}? This cannot be undone and removes all their data.`
      )
    ) {
      return;
    }
    try {
      setActionLoading(true);
      await adminService.deleteUser(record.id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (date: Date) => date.toLocaleString();

  const getTicketStatusBadge = (status: SupportTicketStatus) => (
    <span className={`status-badge ticket-${status}`}>{status.replace('_', ' ')}</span>
  );

  if (!user || userAccountType !== 'admin') {
    return (
      <div className="admin-panel">
        <Navigation />
        <main className="admin-panel-content">
          <div className="admin-panel-container">
            <div className="access-denied">
              <ExclamationTriangleIcon className="error-icon" />
              <h1 className="error-title">Access Denied</h1>
              <p className="error-message">Admin privileges are required to access this page.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-panel">
        <Navigation />
        <main className="admin-panel-content">
          <div className="admin-panel-container">
            <div className="loading-state">
              <div className="loading-spinner" />
              <p className="loading-message">Loading admin dashboard...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <Navigation />
      <main className="admin-panel-content">
        <div className="admin-panel-container">
          <div className="admin-header">
            <h1 className="admin-panel-title">Admin Panel</h1>
            <p className="admin-panel-subtitle">Support tickets, orders, and user management</p>
          </div>

          {error && (
            <div className="admin-inline-error">
              <p>{error}</p>
              <button type="button" onClick={() => setError(null)}>Dismiss</button>
            </div>
          )}

          <div className="stats-overview stats-overview-4">
            <div className="stat-card">
              <div className="stat-icon pending">
                <TicketIcon className="stat-svg-icon" />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{openTickets}</h3>
                <p className="stat-label">Open Tickets</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon total">
                <ShoppingBagIcon className="stat-svg-icon" />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{completedOrders.length}</h3>
                <p className="stat-label">Completed Orders</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon approved">
                <ShoppingBagIcon className="stat-svg-icon" />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">£{totalOrderRevenue.toFixed(2)}</h3>
                <p className="stat-label">Completed Revenue</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon suspended">
                <UserGroupIcon className="stat-svg-icon" />
              </div>
              <div className="stat-content">
                <h3 className="stat-number">{suspendedUsers}</h3>
                <p className="stat-label">Suspended Users</p>
              </div>
            </div>
          </div>

          <div className="admin-tabs">
            <button
              type="button"
              className={`admin-tab ${activeTab === 'tickets' ? 'active' : ''}`}
              onClick={() => setActiveTab('tickets')}
            >
              Support Tickets
            </button>
            <button
              type="button"
              className={`admin-tab ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              Orders
            </button>
            <button
              type="button"
              className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              User Management
            </button>
          </div>

          {activeTab === 'tickets' && (
            <div className="admin-section">
              <div className="admin-filter-tabs">
                {(['all', 'open', 'in_progress', 'resolved', 'closed'] as TicketFilter[]).map(
                  (filter) => (
                    <button
                      key={filter}
                      type="button"
                      className={`admin-filter-tab ${ticketFilter === filter ? 'active' : ''}`}
                      onClick={() => setTicketFilter(filter)}
                    >
                      {filter === 'all' ? 'All' : filter.replace('_', ' ')}
                    </button>
                  )
                )}
              </div>

              {filteredTickets.length === 0 ? (
                <div className="admin-empty-state">
                  <TicketIcon className="admin-empty-icon" />
                  <h3>No tickets found</h3>
                  <p>Support requests and product flags will appear here.</p>
                </div>
              ) : (
                <div className="admin-table">
                  <div className="admin-table-header tickets-grid">
                    <div>Ticket</div>
                    <div>Type</div>
                    <div>Reporter</div>
                    <div>Status</div>
                    <div>Date</div>
                    <div>Actions</div>
                  </div>
                  {filteredTickets.map((ticket) => (
                    <div key={ticket.id} className="admin-table-row tickets-grid">
                      <div className="ticket-cell-main">
                        <strong>{ticket.ticketNumber}</strong>
                        <span>{ticket.subject}</span>
                        {ticket.productName && (
                          <span className="ticket-product-tag">
                            <FlagIcon className="inline-icon" />
                            {ticket.productName}
                          </span>
                        )}
                      </div>
                      <div>{supportTicketService.getTicketTypeLabel(ticket.type)}</div>
                      <div>
                        <div>{ticket.reporterName}</div>
                        <div className="muted-text">{ticket.reporterEmail}</div>
                      </div>
                      <div>{getTicketStatusBadge(ticket.status)}</div>
                      <div>{formatDate(ticket.createdAt)}</div>
                      <div>
                        <button
                          type="button"
                          className="admin-action-btn view"
                          onClick={() => openTicketModal(ticket)}
                        >
                          <EyeIcon className="action-icon" />
                          Manage
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="admin-section">
              <div className="admin-filter-tabs">
                {(
                  [
                    'succeeded',
                    'all',
                    'pending',
                    'failed',
                    'cancelled',
                  ] as OrderFilter[]
                ).map((filter) => (
                  <button
                    key={filter}
                    type="button"
                    className={`admin-filter-tab ${orderFilter === filter ? 'active' : ''}`}
                    onClick={() => setOrderFilter(filter)}
                  >
                    {filter === 'all'
                      ? 'Active'
                      : filter === 'succeeded'
                        ? 'Completed'
                        : filter}
                  </button>
                ))}
              </div>

              {filteredOrders.length === 0 ? (
                <div className="admin-empty-state">
                  <ShoppingBagIcon className="admin-empty-icon" />
                  <h3>No orders found</h3>
                  <p>Customer purchases will appear here once checkout is completed.</p>
                </div>
              ) : (
                <div className="admin-table">
                  <div className="admin-table-header orders-grid">
                    <div>Order</div>
                    <div>Customer</div>
                    <div>Vendors</div>
                    <div>Products</div>
                    <div>Amount</div>
                    <div>Payment</div>
                    <div>Date</div>
                    <div>Actions</div>
                  </div>
                  {filteredOrders.map((order) => (
                    <div key={order.id} className="admin-table-row orders-grid">
                      <div className="order-cell-main">
                        <strong>#{order.id.slice(-8)}</strong>
                        <span>{getOrderStatusBadge(order.status)}</span>
                      </div>
                      <div>
                        <div>{order.customerName}</div>
                        <div className="muted-text">{order.customerEmail}</div>
                      </div>
                      <div className="order-vendors-cell">{formatVendorsSummary(order)}</div>
                      <div className="order-products-cell">{formatProductsSummary(order)}</div>
                      <div className="order-amount-cell">£{order.totalAmount.toFixed(2)}</div>
                      <div>{getPaymentStatusBadge(order.paymentStatus)}</div>
                      <div>{formatDate(order.createdAt)}</div>
                      <div>
                        <button
                          type="button"
                          className="admin-action-btn view"
                          onClick={() => setSelectedOrder(order)}
                        >
                          <EyeIcon className="action-icon" />
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'users' && (
            <div className="admin-section">
              <div className="admin-filter-tabs">
                {(
                  ['all', 'customer', 'vendor', 'admin', 'active', 'suspended'] as UserFilter[]
                ).map((filter) => (
                  <button
                    key={filter}
                    type="button"
                    className={`admin-filter-tab ${userFilter === filter ? 'active' : ''}`}
                    onClick={() => setUserFilter(filter)}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
              </div>

              {filteredUsers.length === 0 ? (
                <div className="admin-empty-state">
                  <UserGroupIcon className="admin-empty-icon" />
                  <h3>No users found</h3>
                </div>
              ) : (
                <div className="admin-table">
                  <div className="admin-table-header users-grid">
                    <div>User</div>
                    <div>Type</div>
                    <div>Status</div>
                    <div>Joined</div>
                    <div>Actions</div>
                  </div>
                  {filteredUsers.map((record) => (
                    <div key={record.id} className="admin-table-row users-grid">
                      <div>
                        <div>{record.displayName}</div>
                        <div className="muted-text">{record.email}</div>
                      </div>
                      <div>
                        <span className={`account-type-badge ${record.accountType}`}>
                          {record.accountType}
                        </span>
                      </div>
                      <div>
                        {record.isActive ? (
                          <span className="status-badge approved">Active</span>
                        ) : (
                          <span className="status-badge suspended">Suspended</span>
                        )}
                      </div>
                      <div>{formatDate(record.createdAt)}</div>
                      <div className="user-actions">
                        {record.accountType !== 'admin' && record.id !== user.uid && (
                          <>
                            {record.isActive ? (
                              <button
                                type="button"
                                className="admin-action-btn suspend"
                                disabled={actionLoading}
                                onClick={() => handleSuspendUser(record.id)}
                              >
                                <NoSymbolIcon className="action-icon" />
                                Suspend
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="admin-action-btn approve"
                                disabled={actionLoading}
                                onClick={() => handleUnsuspendUser(record.id)}
                              >
                                <CheckCircleIcon className="action-icon" />
                                Reactivate
                              </button>
                            )}
                            <button
                              type="button"
                              className="admin-action-btn delete"
                              disabled={actionLoading}
                              onClick={() => handleDeleteUser(record)}
                            >
                              <TrashIcon className="action-icon" />
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="admin-order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Order #{selectedOrder.id.slice(-8)}</h2>
              <button type="button" className="close-modal-btn" onClick={() => setSelectedOrder(null)}>
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="order-detail-grid">
                <div>
                  <strong>Customer</strong>
                  <p>{selectedOrder.customerName}</p>
                  <p className="muted-text">{selectedOrder.customerEmail}</p>
                  {selectedOrder.customerPhone && (
                    <p className="muted-text">{selectedOrder.customerPhone}</p>
                  )}
                </div>
                <div>
                  <strong>Placed</strong>
                  <p>{formatDate(selectedOrder.createdAt)}</p>
                </div>
                <div>
                  <strong>Payment</strong>
                  <p>{getPaymentStatusBadge(selectedOrder.paymentStatus)}</p>
                  {selectedOrder.paymentIntentId && (
                    <p className="muted-text">PI: {selectedOrder.paymentIntentId}</p>
                  )}
                </div>
                <div>
                  <strong>Order status</strong>
                  <p>{getOrderStatusBadge(selectedOrder.status)}</p>
                </div>
              </div>

              <div className="order-detail-section">
                <strong>Products</strong>
                <div className="order-detail-items">
                  {selectedOrder.items.map((item, index) => (
                    <div key={`${item.productId}-${index}`} className="order-detail-item">
                      <div>
                        <span>{item.name}</span>
                        <span className="muted-text"> x{item.quantity}</span>
                      </div>
                      <span>£{item.total.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {selectedOrder.vendorOrders.map((vendorOrder) => (
                <div key={vendorOrder.vendorId} className="order-vendor-block">
                  <strong>{vendorOrder.vendorName}</strong>
                  <p className="muted-text">{vendorOrder.vendorEmail || 'No vendor email'}</p>
                  <div className="order-vendor-totals">
                    <span>Subtotal: £{vendorOrder.subtotal.toFixed(2)}</span>
                    <span>Shipping: £{vendorOrder.shippingCost.toFixed(2)}</span>
                    <span>Platform fee: £{vendorOrder.platformFee.toFixed(2)}</span>
                    <span>Vendor earnings: £{vendorOrder.vendorEarnings.toFixed(2)}</span>
                  </div>
                </div>
              ))}

              <div className="order-detail-totals">
                <div className="total-row">
                  <span>Subtotal</span>
                  <span>£{selectedOrder.subtotal.toFixed(2)}</span>
                </div>
                <div className="total-row">
                  <span>Shipping</span>
                  <span>£{selectedOrder.shippingTotal.toFixed(2)}</span>
                </div>
                <div className="total-row order-grand-total">
                  <span>Total</span>
                  <span>£{selectedOrder.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="approve-btn" onClick={() => setSelectedOrder(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedTicket && (
        <div className="modal-overlay" onClick={() => setSelectedTicket(null)}>
          <div className="admin-ticket-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{selectedTicket.ticketNumber}</h2>
              <button type="button" className="close-modal-btn" onClick={() => setSelectedTicket(null)}>
                ×
              </button>
            </div>
            <div className="modal-content">
              <p><strong>Subject:</strong> {selectedTicket.subject}</p>
              <p><strong>Type:</strong> {supportTicketService.getTicketTypeLabel(selectedTicket.type)}</p>
              <p><strong>From:</strong> {selectedTicket.reporterName} ({selectedTicket.reporterEmail})</p>
              {selectedTicket.productName && (
                <p><strong>Product:</strong> {selectedTicket.productName}</p>
              )}
              <div className="ticket-message-block">
                <strong>Message</strong>
                <p>{selectedTicket.message}</p>
              </div>

              <div className="form-group">
                <label htmlFor="ticket-status" className="form-label">Status</label>
                <select
                  id="ticket-status"
                  className="form-select"
                  value={ticketStatus}
                  onChange={(e) => setTicketStatus(e.target.value as SupportTicketStatus)}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="ticket-notes" className="form-label">Admin notes</label>
                <textarea
                  id="ticket-notes"
                  className="form-textarea"
                  rows={4}
                  value={ticketNotes}
                  onChange={(e) => setTicketNotes(e.target.value)}
                  placeholder="Internal notes about this ticket..."
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="cancel-btn" onClick={() => setSelectedTicket(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="approve-btn"
                disabled={actionLoading}
                onClick={handleSaveTicket}
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
